import {
  IconClose,
  IconDelivery,
  IconErrorOutlined,
  IconHourglass,
  IconQuestionOutlined,
} from '@prisjakt/ui/icon';
import { TranslateFunc } from 'components/translate/src/translate-func';
import { ShippingCollection, StockStatus } from 'src/types';

export enum ShippingType {
  CHEAPEST = 'cheapest',
  FASTEST = 'fastest',
}

enum StockStatusEnum {
  IN_STOCK = 'in_stock',
  INCOMING = 'incoming',
  PREORDER = 'in_stock_preorder',
  UNAVAILABLE = 'not_in_stock',
  UNKNOWN = 'unknown',
}

type DeliveryDays = {
  min: number;
  max: number;
} | null;

export const isStockStatusSupported = (stockStatus?: StockStatus | null) =>
  stockStatus === StockStatusEnum.IN_STOCK;

export const getDeliveryDays = (
  type: ShippingType,
  shippingCollection?: ShippingCollection | null
): DeliveryDays => {
  const selectedShipping = shippingCollection?.[type];
  if (!shippingCollection || !selectedShipping) {
    return null;
  }

  if (
    !selectedShipping.deliveryDays ||
    selectedShipping.deliveryDays.min < 0 ||
    selectedShipping.deliveryDays.max < 0
  ) {
    return null;
  }
  return {
    min: selectedShipping.deliveryDays.min,
    max: selectedShipping.deliveryDays.max,
  };
};

export const getShippingDays = (
  t: TranslateFunc,
  type: ShippingType,
  shippingCollection?: ShippingCollection | null
) => {
  const day = t('product', `delivery-time.days-label`);
  const days = t('product', `delivery-time.days-label-plural`);
  const deliveryDays = getDeliveryDays(type, shippingCollection);

  if (!deliveryDays) {
    return null;
  }
  if (deliveryDays.min === 1 && deliveryDays.max === 1) {
    return `${deliveryDays.max} ${day}`;
  }
  if (deliveryDays.min === 0 && deliveryDays.max === 0) {
    return `${deliveryDays.max} ${days}`;
  }
  if (deliveryDays.min === deliveryDays.max) {
    return `${deliveryDays.max} ${days}`;
  }
  return `${deliveryDays.min}-${deliveryDays.max} ${days}`;
};

export const getStandardizedDeliveryText = (
  t: TranslateFunc,
  stockStatus?: StockStatus | null
) => {
  switch (stockStatus) {
    case StockStatusEnum.IN_STOCK:
      return `0-6 ${t('product', `delivery-time.days-label-plural`)}`;
    case StockStatusEnum.INCOMING:
      return t('product', `delivery-time.incoming`);
    case StockStatusEnum.PREORDER:
      return t('product', `stock-status.pre_order`);
    case StockStatusEnum.UNAVAILABLE:
      return t('product', `delivery-time.unavailable`);
    case StockStatusEnum.UNKNOWN:
    default:
      return t('product', `delivery-time.unknown`);
  }
};

export const getStockStatusBadgeColor = (stockStatus?: StockStatus | null) => {
  switch (stockStatus) {
    case StockStatusEnum.IN_STOCK:
      return 'GREEN';
    case StockStatusEnum.INCOMING:
    case StockStatusEnum.PREORDER:
      return 'YELLOW';
    case StockStatusEnum.UNAVAILABLE:
      return 'RED';
    case StockStatusEnum.UNKNOWN:
      return 'GREY';
    default:
      return undefined;
  }
};

export const getStockStatusIcon = (stockStatus?: StockStatus | null) => {
  switch (stockStatus) {
    case StockStatusEnum.IN_STOCK:
      return IconDelivery;
    case StockStatusEnum.INCOMING:
      return IconHourglass;
    case StockStatusEnum.PREORDER:
      return IconErrorOutlined;
    case StockStatusEnum.UNAVAILABLE:
      return IconClose;
    case StockStatusEnum.UNKNOWN:
      return IconQuestionOutlined;
    default:
      return undefined;
  }
};
